import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { MapPin, Save, Locate, ExternalLink, Clock, Trash2 } from "lucide-react";
import { getCurrentPosition } from "@/lib/geo";

export default function AttendanceSettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/settings");
      setS(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function useMyLocation() {
    try {
      const pos = await getCurrentPosition();
      setS({ ...s, office_lat: Number(pos.lat.toFixed(6)), office_lng: Number(pos.lng.toFixed(6)) });
      toast.success("Lokasi saat ini dipakai");
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/attendance/settings", s);
      setS(data);
      toast.success("Pengaturan absensi disimpan");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function cleanupNow() {
    try {
      const { data } = await api.post("/attendance/cleanup-photos");
      toast.success(
        data.modified
          ? `${data.modified} foto lama berhasil dihapus`
          : "Tidak ada foto lama untuk dihapus"
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (!s) return <div className="text-sm text-ink-500">Memuat…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="attendance-settings-page">
      <PageHeader
        overline="Administrasi"
        title="Pengaturan Absensi"
        description="Atur lokasi kantor (geofencing) dan jam kerja untuk karyawan."
        icon={MapPin}
        tone="brand"
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Lokasi Kantor */}
        <Card className="relative overflow-hidden border-sky-200 bg-gradient-to-br from-sky-50 via-white to-white shadow-none">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-500 to-sky-300" />
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-sky-500 text-white shadow-sm">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <p className="overline text-sky-700">Geofencing</p>
                <h2 className="font-heading text-xl font-bold tracking-tight text-sky-900">Lokasi Kantor</h2>
              </div>
            </div>

            <div>
              <Label>Nama Lokasi</Label>
              <Input value={s.office_name || ""} onChange={(e) => setS({ ...s, office_name: e.target.value })} data-testid="input-office-name" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Latitude</Label>
                <Input type="number" step="0.000001" value={s.office_lat} onChange={(e) => setS({ ...s, office_lat: Number(e.target.value) })} className="font-mono border-sky-200" data-testid="input-office-lat" />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input type="number" step="0.000001" value={s.office_lng} onChange={(e) => setS({ ...s, office_lng: Number(e.target.value) })} className="font-mono border-sky-200" data-testid="input-office-lng" />
              </div>
            </div>

            <div>
              <Label>Radius (meter)</Label>
              <Input type="number" min="10" value={s.office_radius_m} onChange={(e) => setS({ ...s, office_radius_m: Number(e.target.value) })} className="font-mono border-sky-200" data-testid="input-office-radius" />
              <p className="mt-1 text-xs text-ink-500">Karyawan hanya bisa check-in/out di dalam radius ini.</p>
            </div>

            <div>
              <Label>Retensi Foto (hari)</Label>
              <Input
                type="number"
                min="1"
                value={s.photo_retention_days ?? 3}
                onChange={(e) => setS({ ...s, photo_retention_days: Number(e.target.value) })}
                className="font-mono border-sky-200"
                data-testid="input-photo-retention"
              />
              <p className="mt-1 text-xs text-ink-500">
                Foto absensi otomatis dihapus setelah X hari agar database tidak penuh.
                Data absensi (jam, lokasi) tetap tersimpan.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={useMyLocation} data-testid="btn-use-my-location">
                <Locate className="mr-2 h-4 w-4" /> Pakai lokasi saya
              </Button>
              <a
                href={`https://www.google.com/maps?q=${s.office_lat},${s.office_lng}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-brand hover:underline"
              >
                Buka di Google Maps <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </Card>

        {/* Jam Kerja */}
        <Card className="relative overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white shadow-none">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 to-amber-300" />
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-amber-500 text-white shadow-sm">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="overline text-amber-700">Jadwal</p>
                <h2 className="font-heading text-xl font-bold tracking-tight text-amber-900">Jam Kerja</h2>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Jam Datang</Label>
                <Input type="time" value={s.work_start} onChange={(e) => setS({ ...s, work_start: e.target.value })} className="font-mono border-amber-200" data-testid="input-work-start" />
                <p className="mt-1 text-xs text-ink-500">Check-in setelah jam ini = Terlambat.</p>
              </div>
              <div>
                <Label>Jam Pulang</Label>
                <Input type="time" value={s.work_end} onChange={(e) => setS({ ...s, work_end: e.target.value })} className="font-mono border-amber-200" data-testid="input-work-end" />
                <p className="mt-1 text-xs text-ink-500">Check-out sebelum jam ini = Pulang awal.</p>
              </div>
            </div>

            <div className="rounded-md border border-amber-200 bg-white p-3">
              <p className="overline text-amber-700">Preview</p>
              <p className="mt-1 font-mono text-lg font-semibold text-amber-900">
                {s.work_start} – {s.work_end} WIB
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button variant="outline" onClick={cleanupNow} data-testid="btn-cleanup-photos">
          <Trash2 className="mr-2 h-4 w-4" /> Hapus Foto Lama Sekarang
        </Button>
        <Button onClick={save} disabled={saving} className="bg-brand text-white hover:bg-brand-600" data-testid="btn-save-attendance-settings">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan Pengaturan"}
        </Button>
      </div>
    </div>
  );
}
